import React, {
  useState, useEffect, useRef, useContext,
} from 'react';
import PropTypes from 'prop-types';
import orderBy from 'lodash/orderBy';

import { useTranslation } from 'react-i18next';

// ui
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import Tooltip from '@material-ui/core/Tooltip';
import { faChartLine } from '@fortawesome/free-solid-svg-icons';
import { makeStyles } from '@material-ui/core/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import TableContainer from '@material-ui/core/TableContainer';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';

// components
import TableHeaderEnhance from '../../../../components/table/TableHeaderEnhance';
import DamModal from '../../../../components/modal/Modal';
import DamChartDailyMain from '../../chart/daily/DamChartDailyMain';
import DamChartDailyMediumMain from '../../chart/daily/DamChartDailyMediumMain';

// Utils
import formatNumber from '../../../../utils/number/Number';

// style
import { StyledIconButton } from '../../../../components/button/Button';
import styles from '../style';
import {
  StyledFooterTableCell,
  StyledFooterSubTableCell,
} from '../../../../components/table/StyleTable';
import DamLargeColor from '../../../../components/color/dam/DamLargeColor';

// provider
import UserContext from '../../../../context/UserContext';

const useStyles = makeStyles(styles);

export default function DamLatestProvince(props) {
  const { t } = useTranslation();
  const { userIsLogin } = useContext(UserContext);
  const classes = useStyles();
  const unmounted = useRef(false);

  const columns = [
    {
      id: 'dam.dam_name.th',
      disablePadding: false,
      label: t('แหล่งน้ำ'),
      sortable: true,
    },
    {
      title: t('รหัสสถานี'),
      type: 'string',
      field: 'dam.dam_oldcode',
      hidden: false,
      export: false,
      searchable: true,
    },
    {
      id: 'basin.basin_name.th',
      label: t('ลุ่มน้ำ'),
      sortable: true,
      hidden: !userIsLogin,
    },
    {
      id: 'geocode.province_name.th',
      numeric: false,
      disablePadding: false,
      label: t('จังหวัด'),
      sortable: true,
    },
    {
      id: 'dam_storage_percent',
      align: 'right',
      numeric: true,
      disablePadding: false,
      label: t('ปริมาณน้ำ'),
      unit: `(${t('ล้าน ลบ.ม')}/% ${t('ความจุ')})`,
      sortable: true,
    },
    {
      id: 'dam_inflow',
      align: 'right',
      numeric: true,
      disablePadding: false,
      label: t('น้ำไหลลง'),
      unit: `(${t('ล้าน ลบ.ม')})`,
      sortable: true,
    },
    {
      id: 'dam_released',
      align: 'right',
      numeric: true,
      disablePadding: false,
      label: t('น้ำระบาย'),
      unit: `(${t('ล้าน ลบ.ม')})`,
      sortable: true,
    },
    {
      id: 'chart',
      numeric: false,
      disablePadding: false,
      label: '',
    },
  ];

  // table sorting
  const [state, setState] = useState({
    order: 'desc',
    orderBy: 'dam_storage_percent',
  });

  const {
    datas,
  } = props;
  const datasDamNear = datas.dam_daily_near || {};
  const datasDamLarge = datas.dam_daily || {};
  const datasDamMedium = datas.dam_medium || {};

  const [modalData, setModalData] = useState({
    open: false,
    header: t('กราฟอ่างเก็บน้ำรายวัน กรมชลประทาน'),
    subheader: null,
    content: null,
    style: null,
  });

  // set total summary
  // let grandTotalInflow = 0;
  // let grandTotalReleased = 0;
  // let grandTotalDamStorage = 0;
  // let grandTotalMaxStorage = 0;

  // summary value by region
  let totalInflow = 0;
  let totalReleased = 0;
  let totalDamStorage = 0;
  let totalMaxStorage = 0;
  let totalMediumInflow = 0;
  let totalMediumReleased = 0;
  let totalMediumDamStorage = 0;
  let totalNearInflow = 0;
  let totalNearReleased = 0;
  let totalNearDamStorage = 0;

  useEffect(() => {
    if (modalData.open) modalData.open = false;
    return () => { unmounted.current = true; };
  }, [modalData.open]);

  const handleRequestSort = (event, property) => {
    const orderByProperty = property;
    let orderLocal = 'desc';

    if (state.orderBy === property && state.order === 'desc') {
      orderLocal = 'asc';
    }

    setState({ order: orderLocal, orderBy: orderByProperty });
  };

  const handleChart = (data) => (e) => {
    e.preventDefault();

    setModalData({
      open: true,
      header: t('กราฟอ่างเก็บน้ำรายวัน กรมชลประทาน'),
      subheader: null,
      content: <DamChartDailyMain id={data} agencyId={12} />,
    });
  };

  const handleChartMedium = (data) => (e) => {
    e.preventDefault();

    setModalData({
      open: true,
      header: t('กราฟอ่างเก็บน้ำขนาดกลางรายวัน กรมชลประทาน'),
      subheader: null,
      content: <DamChartDailyMediumMain id={data} agencyId={12} />,
    });
  };

  return (
    <>
      <Paper className={classes.root}>
        <TableContainer style={{ maxHeight: 650 }}>
          <Table stickyHeader className={classes.table}>
            {/* table header */}
            <TableHeaderEnhance
              firstColumnSticky
              columnData={columns}
              order={state.order}
              orderBy={state.orderBy}
              onRequestSort={handleRequestSort}
            />
            {/* END table header */}

            <TableBody>
              {/* data near dam */}
              { datasDamNear.length > 0 && (
                <>
                  <TableRow>
                    <StyledFooterSubTableCell className="first-column-sticky" colSpan={7} style={{ backgroundColor: '#C5E1A5' }}>
                      <Box fontWeight="bold">{`${t('แหล่งน้ำอื่นๆ ที่เกี่ยวข้อง')} (${t('ล่าสุด')} ${datas.dam_daily_near[0].dam_date})`}</Box>
                    </StyledFooterSubTableCell>
                  </TableRow>
                  {/* data rows */}
                  {orderBy(datasDamNear, state.orderBy, state.order)
                    .filter((row) => row.agency.id === 12)
                    .map((row) => {
                      const status = DamLargeColor(row.dam_storage_percent);

                      totalNearInflow += row.dam_inflow;
                      totalNearReleased += row.dam_released;
                      totalNearDamStorage += row.dam_storage;

                      // grandTotalInflow += row.dam_inflow;
                      // grandTotalReleased += row.dam_released;
                      // grandTotalDamStorage += row.dam_storage;
                      // grandTotalMaxStorage += row.dam.max_storage;

                      return (
                        <TableRow hover role="checkbox" tabIndex={-1} key={row.id}>
                          <TableCell className="first-column-sticky">
                            <a
                              href="#/"
                              title={row.agency.agency_name.th}
                            >
                              {row.dam.dam_name.th}
                            </a>
                          </TableCell>
                          {userIsLogin
                            && (
                            <TableCell>
                              {row.basin.basin_name.th.replace('ลุ่มน้ำ', '')}
                            </TableCell>
                            )}
                          <TableCell>
                            {row.geocode.province_name.th}
                          </TableCell>
                          <TableCell align="right">
                            <span>
                              {formatNumber(row.dam_storage)}
                              {' '}
                              <span style={{ color: status.color.statusColor }}>
                                {
                                  row.dam_storage_percent ? (
                                    `(${row.dam_storage_percent.toFixed(0)})`
                                  ) : (
                                    ''
                                  )
                                }
                              </span>
                            </span>
                          </TableCell>
                          <TableCell align="right">
                            {(row.dam_inflow) ? row.dam_inflow.toFixed(2) : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {(row.dam_released) ? row.dam_released.toFixed(2) : '-'}
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title={t('กราฟรายปี')}>
                              <StyledIconButton id="button_chart_year" onClick={handleChart(row.dam.id)}>
                                <FontAwesomeIcon icon={faChartLine} style={{ color: '#888' }} />
                              </StyledIconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  {/* END data large dam */}
                  {/* sum large dam */}
                  <TableRow>
                    <StyledFooterTableCell className="first-column-sticky">
                      <Box fontWeight="bold">
                        {t('รวมแหล่งน้ำอื่นๆ ที่เกี่ยวข้อง')}
                      </Box>
                    </StyledFooterTableCell>
                    <StyledFooterTableCell />
                    {userIsLogin
                      && (
                      <StyledFooterTableCell />
                      )}
                    <StyledFooterTableCell align="right">
                      <strong>
                        {formatNumber(totalNearDamStorage.toFixed(0))}
                      </strong>
                    </StyledFooterTableCell>
                    <StyledFooterTableCell align="right">
                      <strong>
                        {totalNearInflow.toFixed(2)}
                      </strong>
                    </StyledFooterTableCell>
                    <StyledFooterTableCell align="right">
                      <strong>
                        {totalNearReleased.toFixed(2)}
                      </strong>
                    </StyledFooterTableCell>
                    <StyledFooterTableCell />
                  </TableRow>
                  {/* END sum near dam */}
                </>
              )}

              {/* data large dam */}
              { datasDamLarge.length > 0 && (
                <>
                  <TableRow>
                    <StyledFooterSubTableCell className="first-column-sticky" colSpan={7} style={{ backgroundColor: '#C5E1A5' }}>
                      <Box fontWeight="bold">{`${t('แหล่งน้ำขนาดใหญ่')} (${t('ล่าสุด')} ${datas.dam_daily[0].dam_date})`}</Box>
                    </StyledFooterSubTableCell>
                  </TableRow>
                  {/* data rows */}
                  {orderBy(datasDamLarge, state.orderBy, state.order)
                    .filter((row) => row.agency.id === 12)
                    .map((row) => {
                      const status = DamLargeColor(row.dam_storage_percent);

                      totalInflow += row.dam_inflow;
                      totalReleased += row.dam_released;
                      totalDamStorage += row.dam_storage;
                      totalMaxStorage += row.dam.max_storage;

                      // grandTotalInflow += row.dam_inflow;
                      // grandTotalReleased += row.dam_released;
                      // grandTotalDamStorage += row.dam_storage;
                      // grandTotalMaxStorage += row.dam.max_storage;

                      return (
                        <TableRow hover role="checkbox" tabIndex={-1} key={row.id}>
                          <TableCell className="first-column-sticky">
                            <a
                              href="#/"
                              title={row.agency.agency_name.th}
                            >
                              {row.dam.dam_name.th}
                            </a>
                          </TableCell>
                          {userIsLogin
                            && (
                            <TableCell>
                              {row.basin.basin_name.th.replace('ลุ่มน้ำ', '')}
                            </TableCell>
                            )}
                          <TableCell>
                            {row.geocode.province_name.th}
                          </TableCell>
                          <TableCell align="right">
                            <span>
                              {formatNumber(row.dam_storage)}
                              {' '}
                              <span style={{ color: status.color.statusColor }}>
                                {
                                  row.dam_storage_percent ? (
                                    `(${row.dam_storage_percent.toFixed(0)})`
                                  ) : (
                                    ''
                                  )
                                }
                              </span>
                            </span>
                          </TableCell>
                          <TableCell align="right">
                            {(row.dam_inflow) ? row.dam_inflow.toFixed(2) : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {(row.dam_released) ? row.dam_released.toFixed(2) : '-'}
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title={t('กราฟรายปี')}>
                              <StyledIconButton id="button_chart_year" onClick={handleChart(row.dam.id)}>
                                <FontAwesomeIcon icon={faChartLine} style={{ color: '#888' }} />
                              </StyledIconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  {/* END data large dam */}
                  {/* sum large dam */}
                  <TableRow>
                    <StyledFooterTableCell className="first-column-sticky">
                      <Box fontWeight="bold">
                        {t('รวมแหล่งน้ำขนาดใหญ่')}
                      </Box>
                    </StyledFooterTableCell>
                    {userIsLogin
                      && (
                      <StyledFooterTableCell />
                      )}
                    <StyledFooterTableCell />
                    <StyledFooterTableCell align="right">
                      <strong>
                        {formatNumber(totalDamStorage.toFixed(0))}
                        <span
                          style={{
                            color: DamLargeColor(
                              parseFloat((totalDamStorage * 100) / totalMaxStorage).toFixed(0),
                            ).color.statusColor,
                          }}
                        >
                          {' ('}
                          {!Number.isNaN(totalMaxStorage, 10) ? ((totalDamStorage * 100) / totalMaxStorage).toFixed(0) : '-'}
                          {'%)'}
                        </span>
                      </strong>
                    </StyledFooterTableCell>
                    <StyledFooterTableCell align="right">
                      <strong>
                        {totalInflow.toFixed(2)}
                      </strong>
                    </StyledFooterTableCell>
                    <StyledFooterTableCell align="right">
                      <strong>
                        {totalReleased.toFixed(2)}
                      </strong>
                    </StyledFooterTableCell>
                    <StyledFooterTableCell />
                  </TableRow>
                  {/* END sum large dam */}
                </>
              )}

              {/* data medium dam */}
              { datasDamMedium.length > 0 && (
                <>
                  <TableRow>
                    <StyledFooterSubTableCell className="first-column-sticky" colSpan={7} style={{ backgroundColor: '#C5E1A5' }}>
                      <Box fontWeight="bold">{`${t('แหล่งน้ำขนาดกลาง')} (${t('ล่าสุด')}  ${datas.dam_medium[0].dam_date})`}</Box>
                    </StyledFooterSubTableCell>
                  </TableRow>
                  {orderBy(datasDamMedium, state.orderBy, state.order)
                    .filter((row) => row.agency.id === 12)
                    .map((row) => {
                      const status = DamLargeColor(row.dam_storage_percent);

                      totalMediumInflow += row.dam_inflow;
                      totalMediumReleased += row.dam_released;
                      totalMediumDamStorage += row.dam_storage;

                      // grandTotalInflow += row.dam_inflow;
                      // grandTotalReleased += row.dam_released;
                      // grandTotalDamStorage += row.dam_storage;

                      return (
                        <TableRow hover role="checkbox" tabIndex={-1} key={row.id}>
                          <TableCell className="first-column-sticky">
                            <a
                              href="#/"
                              title={row.agency.agency_name.th}
                            >
                              {row.dam.dam_name.th}
                            </a>
                          </TableCell>
                          {userIsLogin
                            && (
                            <TableCell>
                              {row.basin.basin_name.th.replace('ลุ่มน้ำ', '')}
                            </TableCell>
                            )}
                          <TableCell>
                            {row.geocode.province_name.th}
                          </TableCell>
                          <TableCell align="right">
                            <span>
                              {row.dam_storage}
                              {' '}
                              <span style={{ color: status.color.statusColor }}>
                                {
                                  row.dam_storage_percent ? (
                                    `(${row.dam_storage_percent.toFixed(0)})`
                                  ) : (
                                    ''
                                  )
                                }
                              </span>
                            </span>
                          </TableCell>
                          <TableCell align="right">
                            {(row.dam_inflow) ? row.dam_inflow.toFixed(2) : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {(row.dam_released) ? row.dam_released.toFixed(2) : '-'}
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title={t('กราฟรายปี')}>
                              <StyledIconButton id="button_chart_year" onClick={handleChartMedium(row.dam.id)}>
                                <FontAwesomeIcon icon={faChartLine} style={{ color: '#888' }} />
                              </StyledIconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  {/* END data medium dam */}

                  {/* sum medium dam */}
                  <TableRow>
                    <StyledFooterTableCell className="first-column-sticky">
                      <Box fontWeight="bold">
                        {t('รวมแหล่งน้ำขนาดกลาง')}
                      </Box>
                    </StyledFooterTableCell>
                    {userIsLogin
                       && (
                       <StyledFooterTableCell />
                       )}
                    <StyledFooterTableCell />
                    <StyledFooterTableCell align="right">
                      <strong>
                        {totalMediumDamStorage.toFixed(2)}
                      </strong>
                    </StyledFooterTableCell>
                    <StyledFooterTableCell align="right">
                      <strong>
                        {totalMediumInflow.toFixed(2)}
                      </strong>
                    </StyledFooterTableCell>
                    <StyledFooterTableCell align="right">
                      <strong>
                        {totalMediumReleased.toFixed(2)}
                      </strong>
                    </StyledFooterTableCell>
                    <StyledFooterTableCell />
                  </TableRow>
                  {/* END sum medium dam */}
                </>
              )}

              { (typeof (datasDamLarge.length) === 'undefined'
                && typeof (datasDamMedium.length) === 'undefined'
                && typeof (datasDamNear.length) === 'undefined') && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <strong>
                      {t('ไม่พบข้อมูลตามเงื่อนไข')}
                    </strong>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <DamModal
          open={modalData.open}
          modalHeader={modalData.header}
          modalSubHeader={modalData.subheader}
          modalContent={modalData.content}
          modalStyle={modalData.style}
        />
      </Paper>
    </>
  );
}

// DamLatestProvince.defaultProps = {
//   handleSearchMarker: null,
// };

DamLatestProvince.propTypes = {
  datas: PropTypes.instanceOf(Object).isRequired,
  // handleSearchMarker: PropTypes.func,
};
